import { useAppForm } from '@/hooks/app-form'
import { useApiMutation } from '@/api/ApiQueryServiceProvider'
import {
  TestFailDeviceFormSchema,
  FORM_CONFIG,
  type TestFailDeviceFormData,
} from './schema'
import { FormConfigLoader } from '@/lib/formConfigLoader'
import {
  renderDateControl,
  renderTextareaControl,
  renderButtonControl,
  type ControlConfig,
} from '../../formRenderFunctions'
import { AppLogger } from '@/lib/logger'

const log = AppLogger.getLogger(import.meta)

export { TestFailDeviceFormSchema, type TestFailDeviceFormData } from './schema'

const validateField = (name: keyof TestFailDeviceFormData, value: unknown): string | null => {
  const shape = TestFailDeviceFormSchema.shape[name]
  if (!shape) return null
  const parsed = shape.safeParse(value)
  if (!parsed.success) {
    return parsed.error.issues[0]?.message ?? 'Invalid value'
  }
  return null
}

export type TestFailDeviceComposerProps = {
  deviceNumber: number
  deviceName?: string
  deviceProcurementInfoNumber?: number
  onSuccess?: () => void
  onError?: (error: Error) => void
  onCancel?: () => void
  showHeader?: boolean
  submitLabel?: string
}

export function TestFailDeviceComposer(props: TestFailDeviceComposerProps) {
  const { deviceNumber, deviceProcurementInfoNumber, onSuccess, onError, onCancel, showHeader = true, submitLabel } = props

  const controlsByGroup = FormConfigLoader.getControlsByGroup(FORM_CONFIG)
  const groups = FormConfigLoader.getGroups(FORM_CONFIG)

  const getDevice = useApiMutation<{ device: any; procurementInfo?: any }>('getDeviceByNumber', 'GET')
  const upsertProcurement = useApiMutation('upsertDeviceProcurementInfo', 'POST')
  const upsertDevice = useApiMutation('upsertDevice', 'POST', {
    invalidates: ['getDevices', 'getDeviceByNumber'],
    onError: (error) => onError?.(error),
  })

  const form = useAppForm<TestFailDeviceFormData>({
    schema: TestFailDeviceFormSchema,
    defaultValues: {
      deviceTestCompletionDate: new Date().toISOString().split('T')[0],
      testFailureReason: '',
    },
    onSubmit: async ({ value }) => {
      const formValue = value as TestFailDeviceFormData
      try {
        const currentDevice = await getDevice.mutateAsync({ deviceNumber })
        const existingDevice: any = (currentDevice as any)?.device || {}
        const existingProcurementInfo: any = (currentDevice as any)?.procurementInfo || {}

        // Record the test result + reason on procurement info.
        // NOTE (backend): `deviceTestStatus` and `deviceTestFailureReason` are NEW
        // fields the backend must add to the procurement-info schema; until then
        // they are ignored by validation.
        const mergedProcurementInfo = {
          ...existingProcurementInfo,
          deviceNumber,
          deviceProcurementInfoNumber:
            deviceProcurementInfoNumber ?? existingProcurementInfo?.deviceProcurementInfoNumber,
          deviceTestingCompletedDate: new Date(formValue.deviceTestCompletionDate).getTime() / 1000,
          deviceTestStatus: 'failed',
          deviceTestFailureReason: formValue.testFailureReason.trim(),
        }
        await upsertProcurement.mutateAsync({ deviceNumber, procurementInfo: mergedProcurementInfo })

        // Transition the device to the failed state (test failure). `failed` already
        // exists in the backend deviceStatus enum, and a device only reaches it via a
        // failed test — provisioning failures live on deviceProvisioningStatus and
        // leave deviceStatus = testing.
        await upsertDevice.mutateAsync({
          device: {
            ...existingDevice,
            deviceNumber,
            deviceStatus: 'failed',
          },
        })

        onSuccess?.()
      } catch (error) {
        log.error('[testFailDevice] Failed to mark test as failed:', error)
        onError?.(error as Error)
      }
    },
  })

  const renderControl = (control: ControlConfig) => {
    if (control.htmlControl === 'date') {
      return (
        <form.AppField
          key={control.name}
          name={control.name as 'deviceTestCompletionDate'}
          validators={{
            onBlur: ({ value }) => validateField(control.name as keyof TestFailDeviceFormData, value) || undefined,
          }}
        >
          {(bound) => renderDateControl({ ...control, required: true }, bound)}
        </form.AppField>
      )
    }

    if (control.htmlControl === 'textarea') {
      return (
        <form.AppField
          key={control.name}
          name={control.name as 'testFailureReason'}
          validators={{
            onBlur: ({ value }) => validateField(control.name as keyof TestFailDeviceFormData, value) || undefined,
          }}
        >
          {(bound) => renderTextareaControl({ ...control, required: true }, bound)}
        </form.AppField>
      )
    }

    return null
  }

  const renderFormContent = () =>
    groups.map((group) => {
      const groupControls = controlsByGroup.get(group.formGroupName) || []
      if (groupControls.length === 0) return null
      return (
        <div key={group.formGroupName} className="space-y-4">
          {groupControls.map((control) => (
            <div key={control.name}>{renderControl(control)}</div>
          ))}
        </div>
      )
    })

  return (
    <form.AppForm>
      <div className="space-y-6">
        {showHeader && (
          <div className="space-y-1">
            <h2 className="text-lg font-semibold">{FORM_CONFIG.formTitle}</h2>
            {FORM_CONFIG.formDescription && (
              <p className="text-sm text-muted-foreground">{FORM_CONFIG.formDescription}</p>
            )}
          </div>
        )}

        {renderFormContent()}

        <div className="pt-4 flex justify-center gap-3">
          {renderButtonControl({
            label: FORM_CONFIG.cancelButton?.label ?? '',
            variant: 'outline',
            onClick: onCancel,
            visibility: !!onCancel,
            type: 'button',
          })}
          {renderButtonControl(
            {
              label: (upsertDevice.isPending || upsertProcurement.isPending)
                ? (FORM_CONFIG.submitButton?.loadingLabel ?? 'Saving...')
                : (submitLabel ?? FORM_CONFIG.submitButton?.label ?? 'Mark Test Failed'),
              variant: 'destructive',
              type: 'submit',
            },
            { SubmitButton: form.SubmitButton },
          )}
        </div>
      </div>
    </form.AppForm>
  )
}
